import {FormNameEnum} from 'enums/form';
import {IFileDetails, IFileTypeRecognition, IResourceMetadata} from 'models/resources';

const toNumericIds = (entries: unknown[] = []): number[] =>
  (entries ?? [])
    .map((entry) => {
      if (entry !== null && typeof entry === 'object' && 'id' in (entry as Record<string, unknown>)) {
        return Number((entry as {id: number | string}).id);
      }
      return Number(entry);
    })
    .filter((id) => !Number.isNaN(id));

const toKsaFormValues = (entries: unknown[] = []): Array<number | {id: number; name: string}> =>
  (entries ?? [])
    .map((entry) => {
      if (entry !== null && typeof entry === 'object' && 'id' in (entry as Record<string, unknown>)) {
        const id = Number((entry as {id: number | string}).id);
        if (Number.isNaN(id)) {
          return null;
        }
        const name =
          (entry as {name?: string; displayText?: string}).name || (entry as {displayText?: string}).displayText || '';
        return name ? {id, name} : id;
      }
      const id = Number(entry);
      return Number.isNaN(id) ? null : id;
    })
    .filter((entry): entry is number | {id: number; name: string} => entry !== null);

export const mapEditFileInitialValues = (
  fileDetails: IFileDetails | null,
  fileMetadata: IResourceMetadata | null,
  fileTypeRecognition: IFileTypeRecognition,
  hasUploadPermissions: boolean
) => ({
  [FormNameEnum.fileType]: fileDetails?.contentType ?? null,
  [FormNameEnum.displayName]: fileDetails?.name ?? null,
  [FormNameEnum.gradeLevels]: toNumericIds(fileMetadata?.gradeLevels),
  [FormNameEnum.competencies]: toNumericIds(fileMetadata?.competencies),
  [FormNameEnum.languages]: toNumericIds(fileMetadata?.languages),
  [FormNameEnum.keyTerms]: fileMetadata?.keyTerms ?? null,
  [FormNameEnum.isAccessibleToStudents]: fileMetadata?.isAccessibleToStudents?.toString() ?? null,
  [FormNameEnum.briefDescription]: fileDetails?.description ?? '',
  [FormNameEnum.programs]: toNumericIds(fileMetadata?.programs),
  [FormNameEnum.pillars]: toNumericIds(fileMetadata?.pillars),
  [FormNameEnum.knowledgeSkillsAndAbilities]: toKsaFormValues(fileMetadata?.kSAs ?? fileMetadata?.ksAs),
  [FormNameEnum.expirationDate]: fileMetadata?.expirationDate ?? null,
  [FormNameEnum.learningObjectives]: fileMetadata?.learningObjectives ?? '',
  [FormNameEnum.shouldDisableFileType]: true,
  [FormNameEnum.webLink]: fileDetails?.url ?? null,
  [FormNameEnum.file]: fileDetails?.file ?? null,
  [FormNameEnum.fileVersion]: fileDetails?.versionNumber ?? null,
  [FormNameEnum.versions]: fileDetails?.versionDetails?.versions ?? null,
  [FormNameEnum.hasUploadPermissions]: hasUploadPermissions ? hasUploadPermissions : false,
  [FormNameEnum.canEdit]: fileDetails?.canEdit ?? false,
  [FormNameEnum.time]:
    fileMetadata?.lengthInMinutes === 0 || fileMetadata?.lengthInMinutes === undefined
      ? null
      : fileMetadata?.lengthInMinutes,
  [FormNameEnum.fileTypeRecognition]: fileTypeRecognition ?? {
    isResource: false,
    isScormPackage: false,
    isWebLink: false,
    isArchive: false,
    isVideo: false,
    isAudio: false,
    isImage: false,
    isPDF: false,
    isFillablePdf: false,
    isDocument: false,
    isExcel: false,
  },

  [FormNameEnum.id]: fileDetails?.id ?? null,
  [FormNameEnum.stockImageLinkNumber]: fileDetails?.imageMetadata?.stockImageLinkNumber ?? null,
  [FormNameEnum.permissionNeeded]:
    fileMetadata?.permissionNeeded === undefined || fileMetadata?.permissionNeeded === null
      ? null
      : String(fileMetadata.permissionNeeded),
  [FormNameEnum.imageSources]: fileDetails?.imageMetadata?.imageSources ?? [],
  [FormNameEnum.ethnicities]: fileDetails?.imageMetadata?.ethnicities ?? [],
  [FormNameEnum.genders]: fileDetails?.imageMetadata?.genders ?? [],
  [FormNameEnum.publishedOutputTool]: fileDetails?.imageMetadata?.publishedOutputTool ?? null,
  [FormNameEnum.alternateText]: fileDetails?.imageMetadata?.alternateText ?? null,
  [FormNameEnum.linkToStockImageSource]: fileDetails?.imageMetadata?.linkToStockImageSource ?? null,

  [FormNameEnum.slideNumber]: fileDetails?.assetClassification?.slideNumber ?? null,
  [FormNameEnum.sessionThemeNumber]: fileDetails?.assetClassification?.sessionThemeNumber ?? null,

  [FormNameEnum.associatedResources]: (fileDetails?.assetTraceability?.associatedResources ?? []).map((entry) => {
    if (entry !== null && typeof entry === 'object' && 'id' in entry) {
      return {
        id: String(entry.id),
        name: entry.name ?? '',
      };
    }
    return String(entry);
  }),
  [FormNameEnum.fileName]: fileDetails?.assetTraceability?.fileName ?? fileDetails?.file?.name ?? null,
  [FormNameEnum.link]: fileDetails?.assetTraceability?.link ?? null,

  [FormNameEnum.characterTraits]: toNumericIds(fileMetadata?.characterTraits),
  [FormNameEnum.behavioralIndicators]: toNumericIds(fileMetadata?.behavioralIndicators),
  [FormNameEnum.videoTranscriptionLink]: fileMetadata?.videoTranscriptionLink ?? null,
  [FormNameEnum.credentialing]:
    fileMetadata?.credentialing === undefined || fileMetadata?.credentialing === null
      ? null
      : String(fileMetadata.credentialing),
  [FormNameEnum.permissionInPerpetuity]: fileMetadata?.permissionInPerpetuity ?? false,
  [FormNameEnum.accessibilityStatus]: fileMetadata?.accessibilityStatus ?? null,
  [FormNameEnum.accessibilityReports]: fileMetadata?.accessibilityReports ?? null,

  [FormNameEnum.urlSource]: fileDetails?.mediaRights?.urlSource ?? null,
  [FormNameEnum.usageType]: fileDetails?.mediaRights?.usageType ?? null,
  [FormNameEnum.reuseFlag]:
    fileDetails?.mediaRights?.reuseFlag === undefined || fileDetails?.mediaRights?.reuseFlag === null
      ? null
      : String(fileDetails.mediaRights.reuseFlag),
  [FormNameEnum.notes]: fileDetails?.mediaRights?.notes ?? null,
  [FormNameEnum.permissionAgreementUrl]: fileDetails?.mediaRights?.permissionAgreementUrl ?? null,
});
